/**
 * Standalone dialog for viewing + downloading Zegen DMS documents
 * attached to an invoice, from the backoffice side.
 *
 * Used from two places today:
 *   - OrderManagementPage listing: an icon button on each INVOICED /
 *     CLOSED order row opens this dialog for the order's invoice.
 *   - OrderDetailDialog: a "View DMS Documents" button in the dialog
 *     footer opens this same standalone dialog.
 *
 * The rendering delegates to the shared <DmsDocumentsPanel> so
 * customers and backoffice see the same JSX -- the only difference
 * is where the fetch data comes from (backoffice endpoint with
 * `refresh=1` support vs customer endpoint).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import DmsDocumentsPanel from '../DmsDocumentsPanel';
import { DmsDocumentResponse } from '../../services/invoiceService';
import { backofficeManagementService } from '../../services/backofficeManagementService';

export interface BackofficeDmsDocumentsDialogProps {
  /**
   * Portal ORDER UUID. The dialog resolves the invoice for the order
   * (via GET /backoffice/orders/{id}/invoice) then fetches DMS docs
   * for that invoice. Passing orderId (vs invoiceId) makes call sites
   * simpler -- the order listing / detail dialog already has the
   * order UUID handy but rarely knows the invoice UUID.
   */
  orderId: string | null;
  /** Human-readable label shown in the dialog title (e.g. order number). */
  contextLabel?: string;
  open: boolean;
  onClose: () => void;
}

const BackofficeDmsDocumentsDialog: React.FC<BackofficeDmsDocumentsDialogProps> = ({
  orderId,
  contextLabel,
  open,
  onClose,
}) => {
  // Every visible label routes through i18next -- the language
  // switcher (used by the rest of the app) picks between Bahasa
  // and English. Keys under `dms` namespace in the locale files.
  const { t } = useTranslation();
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [dmsDocuments, setDmsDocuments] = useState<DmsDocumentResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (forceRefresh: boolean = false) => {
      if (!orderId) return;
      setIsLoading(true);
      setError(null);
      try {
        // Step 1: resolve invoice for this order. Existing endpoint at
        // GET /backoffice/orders/{id}/invoice returns
        // {success, invoice_id, invoice_number, ...} or
        // {success: false, error: "no_invoice_data"} when Epicor
        // hasn't posted an invoice yet / invoice sync hasn't caught it.
        const inv = await backofficeManagementService.getOrderInvoice(orderId);
        if (!inv.success || !inv.invoice_id) {
          setInvoiceId(null);
          setError(inv.message || 'No invoice found for this order');
          setDmsDocuments(null);
          return;
        }
        setInvoiceId(inv.invoice_id);

        // Step 2: fetch DMS documents for that invoice.
        const docs = await backofficeManagementService.getInvoiceDmsDocuments(
          inv.invoice_id,
          forceRefresh,
        );
        setDmsDocuments(docs);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load DMS documents');
        setDmsDocuments(null);
      } finally {
        setIsLoading(false);
      }
    },
    [orderId],
  );

  // Fetch on open. Reset state when the dialog is closed so the next
  // open starts fresh (avoids briefly rendering stale content from
  // a previous order).
  useEffect(() => {
    if (open && orderId) {
      load(false);
    }
    if (!open) {
      setInvoiceId(null);
      setDmsDocuments(null);
      setError(null);
    }
  }, [open, orderId, load]);

  const handleRefresh = useCallback(() => {
    // `refresh=1` forces the backend to re-call Zegen. See the
    // backend endpoint's docstring at backoffice_management.py:
    // backoffice_get_invoice_dms_documents.
    load(true);
  }, [load]);

  const handleDownload = useCallback(
    async (
      docType: 'signed-invoice' | 'delivery-note' | 'faktur-pajak',
      docIndex?: number,
    ) => {
      if (!invoiceId) return;
      try {
        await backofficeManagementService.downloadDmsDocument(
          invoiceId,
          docType,
          docIndex ?? 0,
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to download document');
      }
    },
    [invoiceId],
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t('dms.dialogTitle')}
        {contextLabel && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {contextLabel}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {invoiceId || isLoading ? (
          <DmsDocumentsPanel
            dmsDocuments={dmsDocuments}
            isLoading={isLoading}
            onRefresh={handleRefresh}
            onDownload={handleDownload}
          />
        ) : (
          <Alert severity="info">{t('dms.noInvoiceForOrder')}</Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('dms.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BackofficeDmsDocumentsDialog;
